import { RulebookChapter } from '../types';

/**
 * Content Rewrite 3.0 — prose-first textbook for Reserve 2010.
 * ONE menu chapter per topic / module; internal headings only.
 * Do not link to Special Vintage 2016 chapter IDs (except comparative r2010-vs-2016).
 * Printed tile/expert/magnate/fair values remain PARTIAL — read from components.
 */
export const reserve2010Chapters: RulebookChapter[] = [
  {
    id: 'r2010-before-you-start',
    title: 'Zanim zaczniecie (Reserve 2010)',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-before-main',
        title: 'Wybór wariantu',
        content: [
          {
            type: 'warning',
            text: 'Status treści: PARTIAL. Procedury Reserve 2010 są opisane poniżej. Wartości i efekty nadrukowane wyłącznie na kaflach roczników, ekspertów, magnatów, Fair tiles oraz komponentach modułów odczytuj z fizycznych elementów — aplikacja nie wymyśla ich katalogu.',
          },
          { type: 'heading', text: 'Co wybieracie i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Reserve 2010 to pełny, cięższy wariant Vinhos Deluxe Edition. Używa strony planszy i planszetek gracza oznaczonych jako 2010 Reserve. Ma własne konto bankowe, inwestycje, znaczniki cech, Fair tiles i magnatów na planszy. Nie mieszaj reguł z Special Vintage 2016.',
          },
          { type: 'heading', text: 'Kiedy używać tej strony', level: 2 },
          {
            type: 'paragraph',
            text: 'Wybierzcie Reserve 2010, gdy znacie już rytm Special Vintage 2016 albo chcecie pełniejszą ekonomię z bankiem i cechami. Na pierwszą partię z pudełka zwykle lepiej zacząć od 2016.',
          },
          { type: 'heading', text: 'Jak przygotować pierwszą partię Reserve', level: 2 },
          {
            type: 'checklist',
            items: [
              'Odwróć planszę na stronę Reserve 2010 i użyj tej samej strony planszetek graczy.',
              'Przygotuj znaczniki cech (Feature Markers) i kafle targowe Fair tiles.',
              'Na pierwszą partię wyłącz opcjonalne moduły Experts, Islands, Tasting Room i Connoisseur.',
              'Trzymaj Reference Book wyłącznie do odczytu symboli na komponentach.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład przy stole',
            text: 'Grupa zna już 2016. Odwraca planszę na Reserve 2010, ustawia znaczniki cech i Fair tiles, wyłącza moduły i czyta rozdział „Struktura roku” oraz „Bank” przed pierwszą akcją. Nikt nie otwiera instrukcji 2016 „żeby sprawdzić sprzedaż”.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: mieszać utrzymanie, sprzedaż albo targi z regułami Special Vintage 2016, bo komponenty wyglądają podobnie.',
          },
          {
            type: 'note',
            text: 'Ta instrukcja jest samowystarczalna. Porównanie z 2016 jest tylko w rozdziale „Najważniejsze różnice między wariantami” — nie zastępuje procedur Reserve.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Reserve 2010 = osobna strona planszy, osobne reguły, bez mieszania z 2016. P: Czy potrzebuję instrukcji 2016? O: Nie do gry. P: Czy moduły są wymagane? O: Nie na pierwszą partię.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-game-goal',
    title: 'Cel gry',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-goal-main',
        title: 'Jak wygrać',
        content: [
          { type: 'heading', text: 'Co robisz i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Jesteś portugalskim producentem wina. Przez sześć lat rozwijasz posiadłości, produkujesz wina, sprzedajesz je na rynku krajowym (wpłaty na konto bankowe), eksportujesz za punkty oraz budujesz reputację na trzech targach wina. Wygrywa gracz z największą liczbą VP po punktacji końcowej.',
          },
          { type: 'heading', text: 'Czego nie mylić z celem', level: 2 },
          {
            type: 'bulletList',
            items: [
              'Bagos w ręku i na koncie to narzędzia rozwoju — punkty z konta przyznaje się dopiero na końcu.',
              'Eksport daje VP natychmiast oraz większości kolumn na końcu.',
              'Targi budują Fair Points, które decydują o VP według rankingu.',
              'Sama wysoka produkcja bez sprzedaży i eksportu rzadko wygrywa.',
            ],
          },
          {
            type: 'example',
            title: 'Mini-przykład',
            text: 'Masz wysokie konto i słaby eksport. Rywal ma niższe konto, ale większość w kilku kolumnach eksportu i dobre miejsca na targach. Po rozliczeniu końcowym to on wygrywa — konto nie zastąpiło VP z eksportu i targów.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: liczyć Bagos na koncie jak VP w trakcie gry albo traktować Fair Points jako zwykłe VP.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-end-of-game',
            label: 'Źródła punktów — Koniec gry i punktacja',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Cel: najwyższe VP po trzecich targach i rozliczeniu. P: Czy wygrywa najbogatszy na koncie? O: Nie samo konto. P: Czy Fair Points = VP? O: Nie; dają ranking targów.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-components',
    title: 'Elementy gry Reserve 2010',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-components-main',
        title: 'Co jest używane',
        content: [
          { type: 'heading', text: 'Co leży na stole i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Zanim wykonasz pierwszą akcję, rozpoznaj elementy charakterystyczne dla Reserve 2010. Ta lista nie zastępuje diagramu z pudełka — mówi, do czego służy każdy typ komponentu w tym wariancie.',
          },
          {
            type: 'table',
            headers: ['Element', 'Uwagi Reserve 2010'],
            rows: [
              ['Plansza dwustronna', 'Strona Reserve 2010: Quadrel z polem Bank, tory cech, magnaci.'],
              ['Planszetka gracza', '4 posiadłości, magazyn/piwnica, tor konta i inwestycji.'],
              ['Znaczniki cech', '4 znaczniki — wyłącznie Reserve 2010.'],
              ['Kafle targowe', 'Fair tiles — wyłącznie Reserve 2010.'],
              ['Kafle Action/Multiplier z talii 2016', 'Nie używaj w Reserve 2010.'],
            ],
          },
          {
            type: 'example',
            title: 'Przykład kontroli',
            text: 'Przed startem każdy ma planszetkę Reserve 2010 z czterema posiadłościami i torami konta/inwestycji. Na planszy leżą znaczniki cech i Fair tiles. Jeśli ktoś wyciąga kafle Action/Multiplier z 2016, odłóżcie je z powrotem do pudełka.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: używać kafli Action/Multiplier z Special Vintage 2016 albo mieszać stronę planszetki 2016 z planszą Reserve.',
          },
          {
            type: 'note',
            text: 'Wyjątki od limitu komponentów: Bagos i kafle wina. Pozostałe komponenty są ograniczone liczbą graczy.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Reserve używa konta, cech i Fair tiles; bez talii Action/Multiplier z 2016. P: Czy mogę grać stroną 2016 planszetki? O: Nie. P: Czy Bagos są nieograniczone? O: Tak jako wyjątek limitu.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-board-overview',
    title: 'Plansza i planszetki',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-board-main',
        title: 'Obszary decyzji',
        content: [
          { type: 'heading', text: 'Co widzisz na planszy', level: 2 },
          {
            type: 'paragraph',
            text: 'Plansza łączy miejsca decyzji. Quadrel wybiera akcję (w tym Bank), tory ustalają kolejność, lata i cechy, regiony dają winnice i renomę, a sprzedaż, eksport, targi i magnaci zamieniają wino na konto, punkty albo korzyści.',
          },
          {
            type: 'bulletList',
            items: [
              'Quadrel: Winnice, Winiarnie, Enolodzy, Piwnice, Sprzedaż, Eksport, Bank, Eksperci, Pass/Press Release.',
              'Tor kolejności, tor lat, regiony z renomą.',
              'Rynek sprzedaży, eksport, targi, magnaci na planszy.',
              'Planszetka gracza: 4 posiadłości (nie 5), magazyn/piwnica, tor konta i inwestycji.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład orientacji',
            text: 'Szukasz wypłaty z konta — to pole Bank na Quadrelu i tory na planszetce, nie uproszczona gotówka z 2016. Szukasz cech targowych — to Feature Markers na torach cech, nie system Fair Points z 2016 w oderwaniu od znaczników.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: planować piątą posiadłość jak w Special Vintage 2016 albo szukać na Quadrelu pól Action/Multiplier zamiast magnatów na planszy.',
          },
          {
            type: 'warning',
            text: 'Na planszetce Reserve 2010 są dokładnie 4 posiadłości.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: '4 posiadłości + Bank na Quadrelu + cechy i magnaci. P: Czy jest 5 posiadłości? O: Nie w Reserve. P: Czy Bank jest osobną akcją? O: Tak — pole na Quadrelu.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-setup',
    title: 'Przygotowanie rozgrywki',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-setup-board',
        title: 'Plansza i komponenty wspólne',
        content: [
          { type: 'heading', text: 'Co setup ustala i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Przygotowanie ustawia rocznik, regiony, limity pracowników i budynków oraz tory cech. Błąd tutaj psuje pierwszą produkcję, pierwsze targi i pierwsze odsetki. Wykonaj setup przed wyborem pierwszej akcji.',
          },
          { type: 'heading', text: 'Jak przygotować planszę', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Połóż planszę stroną Reserve 2010 do góry.',
              'Odłóż kafel rocznika 0. Przetasuj pozostałe; odkryj górny.',
              'Ułóż stosy winnic według 9 regionów. Do Douro dołóż Porto.',
              '2 graczy: 7 regionów; 3: 8; 4: 9. Na 1. partię: przy 3 usuń Setúbal; przy 2 Setúbal i Algarve.',
              'Enolodzy 7/10/12, farmerzy 9/12/16, piwnice 8/10/12, winiarnie 11/15/19.',
              '4 stosy ekspertów według cech. Wszystkie kostki renomy w puli. 4 znaczniki cech na torach.',
            ],
          },
        ],
      },
      {
        id: 'r2010-setup-players',
        title: 'Gracze',
        content: [
          { type: 'heading', text: 'Jak przygotować graczy', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Kolor, planszetka Reserve 2010, dyski na 4 startach posiadłości, znacznik akcji na środku Quadrelu.',
              'VP i Fair Points na 0. Kolejność na górnym rzędzie; po wyborze winnic: niższy numer regionu zaczyna.',
              'Pieniądze: 19/20/21/22 Bagos. Ustaw znaczniki konta i inwestycji według diagramu.',
            ],
          },
        ],
      },
      {
        id: 'r2010-setup-initial',
        title: 'Pierwsza posiadłość',
        content: [
          { type: 'heading', text: 'Jak wybrać start', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Wybierz region, połóż renomę, weź darmową winnicę, marker na mapę, wino startowe według cechy regionu.',
            ],
          },
          {
            type: 'note',
            text: 'Lisboa: winiarnia + wino 3. Ribatejo: farmer + wino 3. Douro: 2 lub Porto 5. Dão: piwnica. Setúbal: 2 ekspertów. Nadrukowane wartości odczytuj z komponentów regionu.',
          },
          {
            type: 'example',
            title: 'Przykład kontroli gotowości',
            text: 'Przy trzech graczach Setúbal jest usunięty, każdy ma znacznik na torze kolejności i koncie, pierwszy kafel rocznika jest odkryty, a każdy ma jedną startową posiadłość. Dopiero wtedy ktoś wykonuje pierwszą akcję na Quadrelu.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zostawić wszystkie regiony przy 2–3 graczach albo ustawić konta „na oko” bez diagramu planszetki.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Strona Reserve, limity graczy, cechy, konta, startowa posiadłość. P: Czy rocznik 0 wchodzi do talii? O: Nie — odłóż. P: Czy moduły są w setupie bazowym? O: Nie na pierwszą partię.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-regions',
    title: 'Regiony i premie',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-regions-list',
        title: 'Dziewięć regionów',
        content: [
          { type: 'heading', text: 'Co daje region i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Każdy region wiąże posiadłość z cechą, renomą i premią startową lub trwałym efektem. Wybór regionu na starcie i przy kupnie winnic kształtuje całą partię — nie traktuj regionów jako „tego samego koloru winnicy”.',
          },
          {
            type: 'table',
            headers: ['Nr', 'Region', 'Efekt'],
            rows: [
              ['1', 'Trás-os-Montes', '+2 FP przy wystawieniu wina z regionu.'],
              ['2', 'Minho', 'Bez piwnicy w posiadłości Minho.'],
              ['3', 'Douro', 'Porto +3 jakości; start 2 lub 5.'],
              ['4', 'Dão', 'Darmowa piwnica; start z winem w piwnicy.'],
              ['5', 'Ribatejo', 'Darmowy farmer; start wino 3.'],
              ['6', 'Lisboa', 'Darmowa winiarnia; start wino 3.'],
              ['7', 'Setúbal', '2 darmowych ekspertów.'],
              ['8', 'Alentejo', 'Renoma +2 za kostkę (max 2 kostki).'],
              ['9', 'Algarve', 'Zawsze +1 do wartości wina.'],
            ],
          },
          {
            type: 'example',
            title: 'Przykład wyboru',
            text: 'Bierzesz Dão na start: dostajesz darmową piwnicę i wino już na torze piwnicy. Rywal bierze Minho — nie zbuduje piwnicy w tej posiadłości, więc planuje szybszą sprzedaż lub eksport bez długiego dojrzewania.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: budować piwnicę w Minho albo zapominać o stałym +1 Algarve przy liczeniu wartości.',
          },
          {
            type: 'note',
            text: 'Dokładne nadruki Porto i startowych win odczytuj z komponentów. Odsyłacz: jakość i wartość — rozdział „Jakość i wartość wina”.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Region = cecha + premia + limity. P: Czy Minho może mieć piwnicę? O: Nie w posiadłości Minho. P: Czy Alentejo podwaja każdą renomę? O: +2 za kostkę, max 2 kostki.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-year-structure',
    title: 'Struktura roku',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-year-phases',
        title: 'Fazy',
        content: [
          { type: 'heading', text: 'Co, kiedy i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Partia trwa sześć lat. Każdy rok ustawia kontekst (rocznik, cechy), daje każdemu dwie główne akcje, a na końcu rozlicza utrzymanie (odsetki i pensje) oraz produkcję. Targi wpadają po latach 3., 5. i 6. Nowe wino powstaje po akcjach, nie w ich środku.',
          },
          {
            type: 'numberedList',
            items: [
              'Początek roku (od 2.): nowy rocznik, przesunięcie cech; przed 4. i 6. odśwież ekspertów.',
              'Dwie akcje z ewentualną zmianą kolejności po każdej.',
              'Utrzymanie: odsetki + pensje enologów.',
              'Produkcja: starzenie, pracownicy, wina.',
              'Targi po 3., 5. i 6. roku.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład pełnego roku',
            text: 'W roku 3 odkrywacie rocznik i przesuwacie cechy, każdy robi dwie akcje, płacicie odsetki i pensje z konta, starzejecie wina, produkujecie, a następnie rozgrywacie targi — bo to rok targowy.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: pominąć utrzymanie albo produkować przed starzeniem; albo brać „jedną beczkę ze sprzedaży” jak w 2016 zamiast odsetek/pensji Reserve.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-maintenance',
            label: 'Utrzymanie',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Dwie akcje → utrzymanie → produkcja → ewentualne targi. P: Czy produkuję w środku akcji? O: Nie. P: Czy w roku 2 są targi? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-start-of-year',
    title: 'Początek roku',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-soy-main',
        title: 'Rocznik i cechy',
        content: [
          { type: 'heading', text: 'Co ustawia początek roku', level: 2 },
          {
            type: 'paragraph',
            text: 'Od drugiego roku odkrywasz nowy kafel rocznika (pogoda zwykle w zakresie +2…−2) i przesuwasz Feature Markers według symboli na kaflu. Znaczniki cech nie resetują się do zera — budują kontekst targów przez całą partię.',
          },
          { type: 'heading', text: 'Kiedy odświeżać ekspertów', level: 2 },
          {
            type: 'paragraph',
            text: 'Przed 4. i 6. rokiem odświeżasz zakryte kafle ekspertów zgodnie z regułą odświeżania. Eksperci odrzuceni na targi nie wracają samym odświeżeniem.',
          },
          {
            type: 'example',
            title: 'Przykład cech',
            text: 'Kafel rocznika każe przesunąć dwa znaczniki cech. Przesuwasz je na torach — nie wracają na start. Później Fair tile i eksperci odwołują się do aktualnych pozycji cech.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: resetować cechy do zera na początku roku albo odświeżać ekspertów odrzuconych na targi.',
          },
          {
            type: 'note',
            text: 'Dokładne symbole pogody i cech odczytuj z kafla rocznika. Odsyłacz: targi — „Targi wina”.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Nowy rocznik + przesunięcie cech; przed 4. i 6. odśwież ekspertów. P: Czy cechy wracają na 0? O: Nie. P: Czy rok 1 odkrywa nowy rocznik jak lata 2–6? O: Setup odkrywa startowy; od 2. roku stosujesz procedurę początku roku.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-movement',
    title: 'Ruch na Quadrelu',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-move-rules',
        title: 'Koszty',
        content: [
          { type: 'heading', text: 'Co robi ruch i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Znacznik akcji na Quadrelu wybiera pole, które wykonujesz. Ruch kosztuje Bagos: sąsiedztwo jest darmowe, dalsze pole i zajęte pola kosztują. Nie wolno wejść na pole, jeśli nie stać cię na pełny ruch i wykonanie akcji.',
          },
          {
            type: 'bulletList',
            items: [
              'Musisz zmienić pole (wyjątek: Pass, gdy brak innych legalnych akcji).',
              'Ruch sąsiadująco (ortogonalnie lub po skosie): 0 Bagos.',
              'Ruch dalej: 1 Bago do banku.',
              'Płacisz po 1 Bago każdemu graczowi na docelowym polu.',
              'Znacznik roku na polu: dodatkowo +1 Bago do banku.',
              'Pass/Press Release: wejście bez kosztów ruchu.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład kosztu',
            text: 'Stoisz na Sales, chcesz wejść na odległy Bank, na którym stoi rywal. Płacisz 1 Bago za odległość i 1 Bago rywalowi. Dopiero potem wypłacasz lub wpłacasz, jeśli stać cię na całość.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zostać na tym samym polu, pominąć opłatę dla graczy na polu albo wejść „na kredyt”.',
          },
          {
            type: 'warning',
            text: 'Wejdź tylko gdy stać Cię na ruch i wykonanie akcji.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Zmień pole, zapłać pełny koszt, wykonaj akcję. P: Czy Pass kosztuje ruch? O: Wejście nie generuje kosztów ruchu. P: Czy mogę wejść bez środków na akcję? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-player-order',
    title: 'Kolejność graczy',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-order-main',
        title: 'Pass i zmiana kolejności',
        content: [
          { type: 'heading', text: 'Kiedy zmienia się kolejność', level: 2 },
          {
            type: 'paragraph',
            text: 'Kolejność ustala, kto wykonuje pierwszą i drugą akcję roku. Pass / Press Release przenosi znacznik na dolny rząd. Po rundzie akcji przenosisz znaczniki z dolnego rzędu na górny. Po targach kolejność startową ustala ranking Fair Points: ostatni na FP zaczyna.',
          },
          {
            type: 'example',
            title: 'Przykład po targach',
            text: 'Po rozstrzygnięciu targów gracz z najniższymi Fair Points zaczyna kolejny rok. To nagradza słabszy wynik targowy tempem na Quadrelu — nie myl tego z VP z rankingu targów.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zostawić znaczniki na dolnym rzędzie bez przeniesienia na górny albo ustawiać kolejność po targach według VP zamiast Fair Points.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-press-release',
            label: 'Pass / Press Release',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Pass → dolny rząd → po rundzie na górę; po targach ostatni FP zaczyna. P: Czy VP ustalają kolejność po targach? O: Nie — Fair Points. P: Czy muszę robić Pass, by zmienić kolejność? O: Pass/Press Release jest podstawowym sposobem wejścia na dolny rząd.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-vineyards',
    title: 'Akcja Winnice',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-vineyards-main',
        title: 'Zakup',
        content: [
          { type: 'heading', text: 'Co daje winnica i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Winnica otwiera lub wzmacnia posiadłość w jednym regionie i daje +2 do jakości produkcji. Bez winnicy posiadłość nie produkuje. W jednej akcji możesz kupić winnice z różnych regionów, ale nie dwie z tego samego regionu.',
          },
          { type: 'heading', text: 'Jak kupić', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Weź górną winnicę wybranego regionu, zapłać koszt z kafla.',
              'Połóż na pustym polu posiadłości tej samej cechy koloru/regionu.',
              'Pierwsza winnica regionu: marker na mapę + kostka renomy.',
              'Maksymalnie 2 winnice na posiadłość.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład akcji',
            text: 'Kupujesz jedną winnicę w Lisboa i jedną w Alentejo w tej samej akcji — legalne, bo różne regiony. Nie możesz wziąć drugiej winnicy Lisboa w tej samej akcji.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: kupić dwie winnice z tego samego regionu w jednej akcji albo przekroczyć limit 2 winnic na posiadłość.',
          },
          {
            type: 'note',
            text: 'Każda winnica: +2 do jakości produkcji. Koszty nadrukowane odczytuj z kafla.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Winnica = posiadłość +2 jakości; max 2/posiadłość; nie 2 z jednego regionu w jednej akcji. P: Czy pierwsza winnica daje renomę? O: Tak — marker + renoma. P: Czy winnica od razu daje VP? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-wineries',
    title: 'Akcja Winiarnie',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-wineries-main',
        title: 'Budowa',
        content: [
          { type: 'heading', text: 'Co daje winiarnia', level: 2 },
          {
            type: 'paragraph',
            text: 'Winiarnia podnosi jakość produkcji o +1 i daje miejsce na enologa. Budujesz 1 lub 2 winiarnie w jednej akcji, każda po 3 Bagos.',
          },
          {
            type: 'bulletList',
            items: [
              'Zapłać 3 Bagos za każdą budowaną winiarnię.',
              'Połóż winiarnię w posiadłości zgodnie z limitem miejsc.',
              'Dodaj renomę w regionie posiadłości albo na winiarni bez winnicy — zgodnie z regułą umieszczania renomy z planszy/Reference Book.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład',
            text: 'Budujesz dwie winiarnie za 6 Bagos w dwóch posiadłościach. Każda daje +1 jakość. Później możesz zatrudnić enologów na puste winiarnie.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: budować winiarnię bez wolnego miejsca albo zapominać o +1 jakości przy produkcji.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-enologists',
            label: 'Akcja Enolodzy',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: '1–2 winiarnie × 3 Bagos, +1 jakość. P: Czy mogę zbudować 3 naraz? O: Nie — max 2 na akcję. P: Czy winiarnia produkuje bez winnicy? O: Posiadłość potrzebuje winnicy do produkcji.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-enologists',
    title: 'Akcja Enolodzy',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-enologists-main',
        title: 'Zatrudnienie',
        content: [
          { type: 'heading', text: 'Co robi enolog', level: 2 },
          {
            type: 'paragraph',
            text: 'Enolog daje +2 do jakości produkcji i zajmuje pustą winiarnię. Zatrudniasz 1 lub 2 enologów po 1 Bago. W utrzymaniu każdy enolog kosztuje 1 Bago pensji z konta.',
          },
          {
            type: 'bulletList',
            items: [
              'Zapłać 1 Bago za enologa.',
              'Połóż go na pustą winiarnię.',
              'Bez modułu Tasting Room tą akcją nie zatrudniasz farmerów — farmer bazowo pochodzi z Ribatejo.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład',
            text: 'Masz dwie puste winiarnie. Zatrudniasz dwóch enologów za 2 Bagos. Na utrzymaniu zapłacisz 2 Bagos z konta — jeśli konta braknie, stosujesz regułę utrzymania.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zatrudniać farmerów akcją Enolodzy bez kafla Tasting Room albo płacić pensje z gotówki w ręku zamiast z konta.',
          },
          {
            type: 'warning',
            text: 'Bez Tasting Room nie zatrudniasz farmerów tą akcją — tylko Ribatejo (premia regionu).',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: '1–2 enologów × 1 Bago, +2 jakości, pensja z konta. P: Czy farmerzy są w bazowej akcji? O: Nie bez Tasting Room. P: Czy pensja idzie z ręki? O: Nie — z konta.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-wine-quality',
    title: 'Jakość i wartość wina',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-quality',
        title: 'Jakość przy produkcji',
        content: [
          { type: 'heading', text: 'Co liczy się przy produkcji', level: 2 },
          {
            type: 'paragraph',
            text: 'Jakość opisuje potencjał posiadłości w danym roku. Z niej powstaje kafel wina. Jeśli jakość wyniesie 0, posiadłość nie produkuje użytecznego wina.',
          },
          {
            type: 'paragraph',
            text: 'Jakość = +2 za winnicę +1 za farmera +1 za winiarnię +2 za enologa +3 za Porto (Douro) ± pogoda z rocznika. Nie zaokrąglaj samodzielnie.',
          },
        ],
      },
      {
        id: 'r2010-value',
        title: 'Wartość przy użyciu',
        content: [
          { type: 'heading', text: 'Co liczy się przy sprzedaży, eksporcie i targach', level: 2 },
          {
            type: 'paragraph',
            text: 'Wartość = jakość + starzenie piwnicy (+1/+3/+5 według pól) + renoma (1–2 kostki; Alentejo +2 za kostkę, max 2) +1 Algarve, gdy dotyczy.',
          },
          {
            type: 'warning',
            text: 'Renomy zużywasz tylko gdy są potrzebne do osiągnięcia progu danej operacji — nie „dokładasz zawsze”.',
          },
          {
            type: 'example',
            title: 'Przykład rozróżnienia',
            text: 'Posiadłość ma jakość 6 i produkuje kafel 6. Po dojrzewaniu w piwnicy wartość użytkowa rośnie według pól. Przy eksporcie dokładasz renomę tylko jeśli próg pola tego wymaga.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: liczyć +1 za winnicę zamiast +2, albo traktować renomę jako trwałe podbicie kafla.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Jakość → kafel; wartość = jakość + starzenie + legalna renoma. P: Czy jakość 0 produkuje? O: Nie. P: Czy renoma zawsze wchodzi? O: Tylko do progu.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-cellars-aging',
    title: 'Piwnice i starzenie',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-cellars-main',
        title: 'Akcja Piwnice',
        content: [
          { type: 'heading', text: 'Co robi piwnica i kiedy starzejesz', level: 2 },
          {
            type: 'paragraph',
            text: 'Piwnica przykrywa magazyn posiadłości i pozwala winom dojrzewać na wyższych polach (+1/+3/+5). Budujesz 1 piwnicę za 2 Bagos; przenieś istniejące wina z zachowaniem poziomu starzenia.',
          },
          {
            type: 'bulletList',
            items: [
              'Zapłać 2 Bagos i połóż piwnicę, przykrywając magazyn.',
              'Przenieś wina na odpowiadające pola piwnicy.',
              'Przed produkcją przesuń każde istniejące wino o 1 pole w prawo; wypadanie poza tor = strata.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład kolejności',
            text: 'Budujesz piwnicę w trakcie roku. Na produkcji najpierw przesuwasz stare wina o jedno pole, potem produkujesz nowy kafel na lewe pole. Nie produkujesz najpierw.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: produkcja przed starzeniem albo budowa piwnicy w Minho.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-production',
            label: 'Produkcja',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Piwnica 2 Bagos; starzenie przed produkcją. P: Czy piwnica podnosi jakość produkcji? O: Nie bezpośrednio — wydłuża dojrzewanie. P: Czy wino może wypaść? O: Tak, jeśli zejdzie poza tor.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-sales',
    title: 'Sprzedaż krajowa',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-sales-main',
        title: 'Akcja Sales',
        content: [
          { type: 'heading', text: 'Co daje sprzedaż i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Sprzedaż krajowa zamienia wino na przesunięcie znacznika konta (max 24). To główne źródło wpływu na konto w Reserve 2010 — nie myl z gotówką z uproszczonej sprzedaży 2016.',
          },
          { type: 'heading', text: 'Jak sprzedać', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Wybierz jedno lub więcej własnych win i wolne sloty rynku spełniające próg/kolor.',
              'W razie potrzeby zużyj renomę do progu.',
              'Odrzuć wino, umieść beczkę na slocie, przesuń konto o wartość slotu (max 24).',
              'Odzyskuj tylko pary beczek z tego samego lokalu → 2 renomy w różnych regionach.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład sprzedaży',
            text: 'Sprzedajesz wino na slot o wartości 4. Odrzucasz wino, kładziesz beczkę, przesuwasz konto o 4 (nie przekraczając 24). Później, mając parę beczek w tym samym lokalu, możesz je odzyskać za 2 renomy w różnych regionach.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: brać Bagos do ręki jak w 2016 zamiast przesuwać konto, albo zabierać jedną beczkę w utrzymaniu jak w Special Vintage.',
          },
          {
            type: 'note',
            text: 'Wartości slotów odczytuj z planszy dla liczby graczy. Odsyłacz: wypłaty z konta — „Bank”.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Sprzedaż → konto (max 24); odzysk par beczek → renomy. P: Czy sprzedaż daje gotówkę do ręki? O: Nie — konto. P: Czy utrzymanie zabiera beczki sprzedaży? O: Nie jak w 2016 — Reserve ma odsetki i pensje.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-export',
    title: 'Eksport',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-export-main',
        title: 'Akcja Export',
        content: [
          { type: 'heading', text: 'Co daje eksport', level: 2 },
          {
            type: 'paragraph',
            text: 'Eksport zamienia wino na natychmiastowe VP z wiersza oraz ustawia beczki do walki o większości kolumn na koniec gry. Wykonujesz akcję Export, eksportując jedno lub więcej win na wolne sloty.',
          },
          {
            type: 'numberedList',
            items: [
              'Wybierz własne wina i wolne sloty eksportu spełniające wymagania.',
              'Zużyj renomę tylko jeśli potrzeba do progu.',
              'Odrzuć wina, umieść beczki, weź VP wskazane w wierszu.',
              'Na końcu gry rozlicz większości kolumn; przy 2 graczach stosuj ramkę 2p.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład eksportu',
            text: 'Eksportujesz dwa wina na dwa wolne sloty w różnych kolumnach. Bierzesz VP z wierszy od razu. Beczki zostają — na końcu mogą dać większości kolumn.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: eksportować bez progu, mylić eksport ze sprzedażą na konto albo pomijać ramkę 2p.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Wino → beczka + VP teraz + walka o kolumny później. P: Czy eksport podnosi konto? O: Nie. P: Czy mogę eksportować bez kafla wina? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-bank',
    title: 'Bank',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-bank-main',
        title: 'Akcja Bank',
        content: [
          { type: 'heading', text: 'Po co jest Bank', level: 2 },
          {
            type: 'paragraph',
            text: 'Pole Bank na Quadrelu zarządza kontem i inwestycjami. Odsetki i pensje w utrzymaniu schodzą z konta, nie z gotówki w ręku. Bez rezerwy na koncie utrzymanie może kosztować VP i budynki.',
          },
          { type: 'heading', text: 'Wypłata i wpłata', level: 2 },
          {
            type: 'paragraph',
            text: 'Wypłata zamienia środki z konta na bagos w ręku. Wpłata robi odwrotnie. Pole z −2 VP przy wypłacie jest karą za sięganie po gotówkę w trudniejszej sytuacji — odczytaj je z planszetki, nie zgaduj.',
          },
          {
            type: 'bulletList',
            items: [
              'Wypłata: konto −1, +1 Bago do ręki. Wejście na pole −2 VP = −2 VP.',
              'Wpłata: 1 Bago z ręki → konto +1 (max 24).',
            ],
          },
          { type: 'heading', text: 'Inwestycje', level: 2 },
          {
            type: 'paragraph',
            text: 'Inwestycje ustawiasz na torze inwestycji planszetki gracza. Ikony między polami mówią, kiedy wolno inwestować albo wycofać środki (divest) podczas akcji Bank. Wysokość inwestycji wpływa na odsetki w utrzymaniu — im wyższy tor, tym większe wymaganie środków na koncie.',
          },
          {
            type: 'numberedList',
            items: [
              'Wejdź legalnie na pole Bank i zapłać koszt ruchu (jeśli dotyczy).',
              'Wybierz operację: wypłata, wpłata albo inwestycja/divest według aktualnych ikon między polami toru.',
              'Przesuń znaczniki konta i inwestycji dokładnie według ikonografii planszetki.',
              'Nie przekraczaj limitu konta (max 24) i nie pobieraj wypłaty, której nie pokrywa konto.',
              'Zapamiętaj, że w utrzymaniu odsetki i pensje schodzą wyłącznie z konta.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład wypłaty i inwestycji',
            text: 'Potrzebujesz 3 Bagos na ruch i winiarnię. Wchodzisz na Bank, wypłacasz 3 z konta (konto spada o 3, otrzymujesz 3 Bagos). W tej samej akcji, jeśli ikony toru na to pozwalają, możesz też przesunąć znacznik inwestycji — ale nie domyślaj wolnego ruchu inwestycji bez ikony.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: płacić odsetki lub pensje z ręki zamiast z konta, inwestować poza dozwolonymi ikonami toru albo mylić Bank Reserve z uproszczoną gotówką 2016.',
          },
          {
            type: 'note',
            text: 'Odsetki i pensje tylko z konta, nie z gotówki w ręku. Dokładne ikony inwestycji i progi odsetek odczytuj z planszetki — aplikacja nie wymyśla ich katalogu.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-maintenance',
            label: 'Utrzymanie',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Bank = wypłata/wpłata/inwestycje; utrzymanie z konta. P: Czy konto punktuje w trakcie roku? O: Nie — na końcu. P: Czy max konta to 24? O: Tak. P: Czy mogę inwestować bez ikony? O: Nie — tylko według toru.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-experts',
    title: 'Eksperci winiarscy',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-experts-hire',
        title: 'Zatrudnienie i limity',
        content: [
          { type: 'heading', text: 'Co robi ekspert', level: 2 },
          {
            type: 'paragraph',
            text: 'Ekspert daje zdolność specjalną oraz cechę używaną na targach. Zatrudniasz 1–2 ekspertów po 1 Bago akcją Eksperci. Limit to 6 ekspertów przed sobą. W jednej turze możesz użyć wielu odkrytych zdolności.',
          },
          {
            type: 'bulletList',
            items: [
              'Zapłać 1 Bago za eksperta (do 2 na akcję).',
              'Trzymaj maksymalnie 6 ekspertów przed sobą.',
              'Aby użyć zdolności, odwróć kafel zgodnie z jej timingiem.',
              'Przed 4. i 6. rokiem odśwież zakryte kafle; odrzuceni na targi nie wracają samym odświeżeniem.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład tury',
            text: 'Masz trzech odkrytych ekspertów. W jednej turze legalnie odwracasz dwóch z nich w momentach wskazanych przez kafle — Reserve pozwala na wielu w turze, o ile limity i timingi są spełnione.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: trzymać więcej niż 6 ekspertów albo zakładać „tylko jeden ekspert na turę” jak w typowej grze 2016.',
          },
          {
            type: 'note',
            text: 'Teksty zdolności odczytuj z kafli. Opcjonalny moduł Experts ma osobny rozdział, gdy jest włączony.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: '1–2 ekspertów × 1 Bago, limit 6, wielu na turę. P: Czy odrzucony na targi wraca przy odświeżeniu? O: Nie. P: Czy to rozdział o module Experts? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-press-release',
    title: 'Pass / Press Release',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-pr-main',
        title: 'Procedura',
        content: [
          { type: 'heading', text: 'Co robi Press Release i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Pass zmienia kolejność. Press Release (raz na nadchodzące targi) deklaruje jedno wino, buduje Fair Points i stoisko oraz może sięgnąć po magnatów. Wczesna deklaracja zajmuje stoisko wcześniej; późniejsza pozwala wystawić dojrzalsze wino.',
          },
          {
            type: 'numberedList',
            items: [
              'Przenieś znacznik kolejności na dolny rząd.',
              'Wybierz Pass albo Press Release (raz na dane targi).',
              'Przy Press Release: zadeklaruj 1 wino, zużyj legalne renomy, dodaj FP za WV>9 według reguły planszy, weź Fair tile, zajmij stoisko, rozpatrz magnatów (od 2. roku, max 2 beczki), odrzuć wino.',
            ],
          },
          {
            type: 'warning',
            text: 'W 1. roku bez beczek na magnatach.',
          },
          {
            type: 'example',
            title: 'Przykład timingów',
            text: 'W roku 2 robisz wczesny Press Release, by zająć stoisko i wziąć Fair tile. Rywal czeka bliżej targów z wyższą wartością wina. Oba podejścia są legalne.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: robić Press Release dwa razy na te same targi, brać magnatów w roku 1 albo wystawić więcej niż jedno wino.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-magnates',
            label: 'Magnaci — korzyści',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Pass = kolejność; Press Release = jedno wino + FP + stoisko + ewentualni magnaci. P: Czy w roku 1 są magnaci? O: Bez beczek na magnatach. P: Czy mogę dwa Press Release na te same targi? O: Nie — raz na targi.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-magnates',
    title: 'Magnaci — korzyści',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-magnates-main',
        title: 'Dodatkowe akcje i mnożniki',
        content: [
          { type: 'heading', text: 'Co dają magnaci', level: 2 },
          {
            type: 'paragraph',
            text: 'Magnaci na planszy Reserve dają dodatkowe akcje i mnożniki przez beczki umieszczone w ich rzędach. To nie zastępuje akcji na Quadrelu — to korzyść powiązana z Press Release / końcem gry według reguł magnatów.',
          },
          {
            type: 'paragraph',
            text: 'Aby wziąć korzyść: odrzuć 1 wino, przenieś 1 beczkę w tym samym rzędzie na Additional Action lub Multiplier. Maksymalnie 1 korzyść na akcję tury.',
          },
          {
            type: 'example',
            title: 'Przykład',
            text: 'Masz beczkę u magnata. W swojej turze odrzucasz legalne wino i przesuwasz beczkę na Additional Action w tym samym rzędzie — wykonujesz wskazaną korzyść. Nie możesz w tej samej akcji tury wziąć drugiej korzyści magnata.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: traktować magnata jako zamiennik pola Quadrelu albo brać więcej niż jedną korzyść na akcję tury.',
          },
          {
            type: 'note',
            text: 'Teksty Additional Action i Multiplier odczytuj z planszy. Na końcu gry dodatkowe korzyści magnatów trwają, aż wszyscy spasują — patrz „Koniec gry”.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Odrzuć wino → przesuń beczkę w rzędzie → 1 korzyść/akcję. P: Czy magnat zastępuje Quadrel? O: Nie. P: Czy mogę dwie korzyści naraz? O: Nie — max 1 na akcję tury.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-maintenance',
    title: 'Utrzymanie',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-maint-main',
        title: 'Odsetki i pensje',
        content: [
          { type: 'heading', text: 'Kiedy i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Po dwóch akcjach, przed produkcją, rozliczasz utrzymanie z konta. To nie jest „zabierz jedną beczkę ze sprzedaży” z 2016 — to odsetki z inwestycji i pensje enologów.',
          },
          {
            type: 'numberedList',
            items: [
              'Odsetki według toru inwestycji; jeśli brak środków na koncie → −2 VP i strata winiarni/winnicy według reguły planszy.',
              'Pensje enologów z konta (1 Bago na enologa); nie zwalniaj enologa, jeśli stać Cię na pensje.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład braku środków',
            text: 'Masz 1 Bago na koncie, a odsetki i pensje wymagają 3. Nie dobierasz z ręki. Stosujesz karę −2 VP i utratę budynku zgodnie z regułą utrzymania, potem przechodzicie do produkcji.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: płacić utrzymanie z ręki, zabierać beczki sprzedaży jak w 2016 albo zwalniać enologów mimo wystarczającego konta.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-bank',
            label: 'Bank',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Odsetki + pensje z konta przed produkcją. P: Czy mogę zapłacić z ręki? O: Nie. P: Czy to utrzymanie beczek sprzedaży z 2016? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-production',
    title: 'Produkcja',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-prod-main',
        title: 'Kolejność',
        content: [
          { type: 'heading', text: 'Co produkcja robi i kiedy', level: 2 },
          {
            type: 'paragraph',
            text: 'Po utrzymaniu każda posiadłość z winnicą może wyprodukować wino według jakości. Najpierw starzejesz istniejące wina, potem przestawiasz pracowników, potem produkujesz. To nie jest akcja na Quadrelu.',
          },
          {
            type: 'numberedList',
            items: [
              'Starzenie: przesuń istniejące wina o 1 w prawo.',
              'Przestawienie pracowników (enolodzy/farmerzy; turyści jeśli moduł).',
              'Produkcja win dla posiadłości z winnicą według jakości (0 = brak wina).',
            ],
          },
          {
            type: 'example',
            title: 'Przykład',
            text: 'Posiadłość ma winnicę, winiarnię i enologa. Po zsumowaniu jakości i pogody bierzesz kafel i kładziesz go na lewym polu. Jeśli ktoś chciał „najpierw produkować, potem starzeć” — wróć do kroku 1.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: produkcja przed starzeniem albo liczenie +1 za winnicę zamiast +2.',
          },
          {
            type: 'crossReference',
            chapterId: 'r2010-wine-quality',
            label: 'Jakość i wartość wina',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Starzenie → pracownicy → produkcja. P: Czy jakość 0 daje wino? O: Nie. P: Czy produkuję w akcji Winnice? O: Nie — na końcu roku.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-wine-fairs',
    title: 'Targi wina',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-fair-main',
        title: 'Przebieg',
        content: [
          { type: 'heading', text: 'Kiedy i jak rozstrzygasz targi', level: 2 },
          {
            type: 'paragraph',
            text: 'Targi odbywają się po 3., 5. i 6. roku. Fair tiles, cechy i eksperci budują Fair Points; ranking daje VP. Fair Points nie zerują się między targami.',
          },
          {
            type: 'numberedList',
            items: [
              'Uzupełnij Press Release dla graczy, którzy jeszcze nie zadeklarowali wina.',
              'Graj ekspertów potajemnie do limitu wskazanego przez Fair tile.',
              'Przesuń cechy ze strzałkami według reguł kafli.',
              'Przelicz Fair Points i przyznaj VP z rankingu.',
              'Ustal nową kolejność: ostatni na FP zaczyna.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład 2 graczy',
            text: 'Przy dwóch graczach nagrody odpowiadają 1. i 3. miejscu tabeli — nie pomijajcie tej reguły. Fair Points zostają na torze do kolejnych targów.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zerować Fair Points po każdym targu, wystawić więcej niż jedno wino albo stosować system targów z 2016.',
          },
          {
            type: 'note',
            text: 'Teksty Fair tiles i tabel VP odczytuj z komponentów. Aplikacja nie tworzy niezweryfikowanego katalogu tych wartości.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Uzupełnij deklaracje → eksperci → cechy → ranking VP → kolejność. P: Czy FP się zerują? O: Nie. P: Czy przy 2p biorę nagrody 1. i 2.? O: 1. i 3. miejsca.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-end-of-game',
    title: 'Koniec gry i punktacja',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-end-main',
        title: 'Po 3. targach',
        content: [
          { type: 'heading', text: 'Kiedy gra się kończy', level: 2 },
          {
            type: 'paragraph',
            text: 'Gra kończy się po rozstrzygnięciu trzecich targów (po 6. roku). Najpierw gracze mogą brać dodatkowe korzyści magnatów, aż wszyscy spasują. Potem rozliczacie konto, większości eksportu i mnożniki.',
          },
          {
            type: 'numberedList',
            items: [
              'Dokończ trzecie targi.',
              'Dodatkowe korzyści magnatów aż wszyscy spasują.',
              'Rozlicz konto według tabeli końcowej.',
              'Rozlicz większości kolumn eksportu (ramka 2p przy dwóch graczach).',
              'Rozlicz mnożniki i pozostałe efekty końcowe z planszy.',
              'Remis: najpierw eksport, potem pieniądze po divest.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład zamknięcia',
            text: 'Po trzecich targach ktoś jeszcze bierze korzyść magnata, potem wszyscy pasują. Liczycie VP z konta, większości eksportu i mnożników. Nie odkrywacie siódmego rocznika.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zaczynać rok 7, punktować wszystkie pozostałe wina bez podstawy albo pomijać fazę korzyści magnatów przed rozliczeniem.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: '3. targi → magnaci do pasa → konto + eksport + mnożniki. P: Czy jest rok 7? O: Nie. P: Czy remis rozstrzyga konto przed eksportem? O: Nie — najpierw eksport, potem pieniądze po divest.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-common-mistakes',
    title: 'Typowe błędy',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-mistakes-main',
        title: 'Checklista',
        content: [
          { type: 'heading', text: 'Po co ta lista', level: 2 },
          {
            type: 'paragraph',
            text: 'Poniższe punkty zbierają błędy, które najczęściej psują pierwszą partię Reserve 2010. Przeczytajcie je przed rokiem 3 albo gdy ktoś miesza reguły z 2016.',
          },
          {
            type: 'checklist',
            items: [
              'Nie mieszaj utrzymania/sprzedaży z regułami 2016.',
              'Jakość: +2 za winnicę.',
              'Limit 6 ekspertów; wielu na turę.',
              'Renomy tylko do progu.',
              'Magnat nie zastępuje akcji Quadrelu.',
              '4 posiadłości — nie planuj piątej.',
              'Odsetki i pensje z konta, nie z ręki.',
              'Fair Points nie zerują się po targach.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład naprawy',
            text: 'Ktoś zabrał „jedną beczkę ze sprzedaży” w utrzymaniu. Cofacie ten krok: liczcie odsetki i pensje z konta, potem starzenie i produkcję.',
          },
          {
            type: 'note',
            text: 'Odsyłacz: szczegóły procedur są w rozdziałach tematycznych; ta lista nie zastępuje reguł.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Sprawdź bank, jakość +2, limity ekspertów i brak mieszania z 2016. P: Czy jeden błąd unieważnia partię? O: Naprawcie stan i grajcie dalej. P: Gdzie porównanie wariantów? O: Rozdział różnic.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-vs-2016',
    title: 'Najważniejsze różnice między wariantami',
    requiredVariantIds: ['reserve-2010'],
    sections: [
      {
        id: 'r2010-diff-main',
        title: 'Porównanie pomocnicze',
        content: [
          { type: 'heading', text: 'Po co ten rozdział', level: 2 },
          {
            type: 'paragraph',
            text: 'Sekcja wyłącznie porównawcza — nie zastępuje instrukcji Reserve 2010. Pomaga osobom, które znają Special Vintage 2016, uniknąć przenoszenia złych nawyków.',
          },
          {
            type: 'table',
            headers: ['Temat', 'Reserve 2010', 'Special Vintage 2016'],
            rows: [
              ['Posiadłości', '4', '5'],
              ['Bank', 'Pełne konto i inwestycje', 'Uproszczona ekonomia'],
              ['Farmerzy', 'Ribatejo (bazowo)', 'Akcja pracowników'],
              ['Eksperci', 'Limit 6; wielu/turę', 'Bez limitu liczby; zwykle 1/turę'],
              ['Targi', 'Fair tiles + cechy', 'Inny system'],
              ['Magnaci', 'Beczki na planszy', 'Talie kafli'],
              ['Utrzymanie', 'Odsetki + pensje z konta', 'Jedna beczka ze sprzedaży'],
            ],
          },
          {
            type: 'example',
            title: 'Przykład pułapki',
            text: 'Gracz z 2016 w utrzymaniu sięga po beczkę sprzedaży. Zatrzymajcie grę: w Reserve nie ma tej procedury — są odsetki i pensje.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: traktować tę tabelę jako pełną instrukcję zamiast czytać rozdziały proceduralne Reserve.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Porównanie pomaga, ale grasz według rozdziałów r2010-*. P: Czy mogę mieszać reguły? O: Nie. P: Czy tabela zastępuje Bank? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-module-experts',
    title: 'Dodatek Experts (Reserve 2010)',
    requiredVariantIds: ['reserve-2010'],
    requiredModuleIds: ['experts'],
    sections: [
      {
        id: 'r2010-mod-exp',
        title: 'Przygotowanie i różnice',
        content: [
          { type: 'heading', text: 'Co dodaje moduł i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Pakiet Experts jest zgodny z Reserve 2010. Dodaje 32 kafle (po 4 egzemplarze ośmiu zdolności) do ekspertów podstawowych. Poza przygotowaniem stosów i treścią zdolności reguły zatrudniania, limitu 6 i odświeżania pozostają jak w bazowym Reserve.',
          },
          { type: 'heading', text: 'Przygotowanie stosów', level: 2 },
          {
            type: 'paragraph',
            text: 'Przed partią wybierzcie jeden sposób budowy czterech stosów cech: zaskakujący (osobne 7 na cechę), zbalansowany (te same 7 zdolności we wszystkich cechach) albo kontrolowany (grupa wybiera 7 zdolności).',
          },
          { type: 'heading', text: 'Różnica Reserve: Reklamodawca', level: 2 },
          {
            type: 'note',
            text: 'Advertiser (Reklamodawca): w Reserve 2010 bierzesz korzyść magnata bez wydawania wina. Procedura różni się od Special Vintage 2016 — nie przenoś reguły 2016.',
          },
          {
            type: 'paragraph',
            text: 'Pozostałe zdolności (m.in. Marketingowiec +1 VP za beczkę eksportu tej akcji, Sprzedawca +1 Bago za beczkę sprzedaży tej akcji, Szpieg kopiuje odkrytego eksperta rywala bez odwracania go) stosuj według tekstu kafla; przy stole potwierdź timing na komponencie.',
          },
          {
            type: 'example',
            title: 'Przykład Marketingowca',
            text: 'Odwracasz Marketingowca i eksportujesz dwie beczki w tej akcji. Dostajesz VP z wierszy oraz +2 VP premii. Premia nie dotyczy beczek z wcześniejszych tur.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: stosować procedurę Reklamodawcy z 2016 albo uznać, że Szpieg odwraca kafel przeciwnika.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: '32 kafle + trzy sposoby przygotowania; Reklamodawca w Reserve = korzyść magnata bez wina. P: Czy limit 6 znika? O: Nie. P: Czy Szpieg zużywa eksperta rywala? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-module-islands',
    title: 'Dodatek Islands (Reserve 2010)',
    requiredVariantIds: ['reserve-2010'],
    requiredModuleIds: ['islands'],
    sections: [
      {
        id: 'r2010-mod-isl',
        title: 'Madera i Azory',
        content: [
          { type: 'heading', text: 'Co dodaje moduł', level: 2 },
          {
            type: 'paragraph',
            text: 'Islands jest zgodny z Reserve 2010. Dodaje Maderę i/lub Azory jako opcjonalne regiony. Każda użyta wyspa zastępuje jeden z regionów wyłączonych dla liczby graczy i dostaje numer regionu (dla Caroliny i efektów numerowanych).',
          },
          { type: 'heading', text: 'Madera', level: 2 },
          {
            type: 'bulletList',
            items: [
              'Zakup winnicy: 6 Bagos; potem 1 turysta w piwnicy (lub w magazynie Madery, jeśli brak piwnicy).',
              'Turystę z magazynu możesz przenieść do piwnicy podczas produkcji.',
              'Turysta w piwnicy: +1 wartości wina zabieranego z tej piwnicy (nie do produkcji).',
              'W każdej produkcji: 1 Bago z każdej posiadłości na Maderze, niezależnie od turysty.',
            ],
          },
          { type: 'heading', text: 'Azory', level: 2 },
          {
            type: 'bulletList',
            items: [
              'Zakup winnicy: 4 Bagos; specjalny magazyn 3 pól; beczka gracza na polu 3.',
              'Każda produkcja: wybór czerwone lub białe; tylko winnice; bez piwnicy.',
              'Gdy wino dojrzeje do pola 9 z beczką: jednorazowy darmowy eksport (specjalna beczka Azorów), bez akcji Export na Quadrelu; renoma Azorów nadal dozwolona do progu.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład Azorów',
            text: 'Wino z Azorów dochodzi do pola 9. Natychmiast możesz wykonać darmowy eksport raz na grę tą beczką — nie zajmujesz pola Export na Quadrelu.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: dodać wyspy ponad limit regionów zamiast zastąpić wyłączone, albo traktować eksport Azorów jako akcję Quadrelu.',
          },
          {
            type: 'note',
            text: 'Turysta Madery to ten sam typ pionka co w Tasting Room — przy obu modułach obowiązuje limit 1 turysty na piwnicę.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Wyspy zastępują wyłączone regiony; Madera = turysta + 1 Bago; Azory = kolor + jednorazowy eksport z 9. P: Czy Madera daje Bago tylko z turystą? O: Nie. P: Czy darmowy eksport wymaga akcji? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-module-tasting-room',
    title: 'Dodatek Tasting Room (Reserve 2010)',
    requiredVariantIds: ['reserve-2010'],
    requiredModuleIds: ['tasting-room'],
    sections: [
      {
        id: 'r2010-mod-tr',
        title: 'Turyści',
        content: [
          { type: 'heading', text: 'Co dodaje moduł w Reserve', level: 2 },
          {
            type: 'paragraph',
            text: 'Tasting Room jest zgodny z Reserve 2010. Połóż kafel Enolodzy/Farmerzy/Turyści stroną 2010. Z tym kaflem wolno zatrudniać farmerów akcją pracowników — to kluczowa różnica względem bazowego Reserve bez modułu.',
          },
          {
            type: 'bulletList',
            items: [
              'Odłóż tylu turystów, ile łącznie piwnic w puli (uwzględnij Maderę, jeśli aktywna).',
              'Turysta kosztuje 2 Bagos; może stanąć na dowolnym polu posiadłości, ale działa tylko w piwnicy.',
              'Maks. 1 turysta na piwnicę; +1 wartości przy zabieraniu wina z odwiedzanej piwnicy.',
              'Premia nie podnosi jakości produkcji i nie zapisuje się na kaflu.',
              'Turysta nie wchodzi do podwójnej piwnicy Connoisseur.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład',
            text: 'Zatrudniasz farmera i turystę w jednej akcji dzięki kaflowi 2010. Turysta wchodzi do piwnicy; przy eksporcie wina z tej piwnicy liczy się +1 tylko do tej czynności.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: doliczać turystę do jakości produkcji albo zatrudniać farmerów bez kafla Tasting Room.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Strona 2010 kafla = farmerzy dozwoleni; turysta +1 przy zabieraniu z piwnicy. P: Czy turysta podnosi produkcję? O: Nie. P: Czy bez modułu mogę brać farmerów akcją? O: Nie — tylko Ribatejo.',
          },
        ],
      },
    ],
  },
  {
    id: 'r2010-module-connoisseur',
    title: 'Dodatek Connoisseur (Reserve 2010)',
    requiredVariantIds: ['reserve-2010'],
    requiredModuleIds: ['connoisseur'],
    sections: [
      {
        id: 'r2010-mod-con',
        title: 'Podwójne kafle',
        content: [
          { type: 'heading', text: 'Co dodaje moduł', level: 2 },
          {
            type: 'paragraph',
            text: 'Connoisseur jest zgodny z Reserve 2010. Dodaje podwójną winnicę, winiarnię i piwnicę oraz opcjonalny rocznik Filoksera. Każdy gracz max po jednym kaflu każdego rodzaju.',
          },
          {
            type: 'bulletList',
            items: [
              'Podwójna winnica: 6 Bagos, +3 jakości, 2 miejsca, 1 farmer, +1 renoma; wymaga istniejącej winnicy; bez miejsca na winiarnię w tej posiadłości.',
              'Podwójna winiarnia: 5 Bagos, +2 jakości, 2 miejsca, 1 enolog, +1 renoma.',
              'Podwójna piwnica: 4 Bagos, dojrzewanie +2/+2/+4/+6, +1 renoma; turyści nie wchodzą.',
              'Podwójny kafel liczy się jako jeden kafel przy punktowaniu, zajmuje dwa miejsca.',
              'Filoksera (opcjonalnie): w roku odkrycia brak produkcji; targi możliwe z wcześniejszym winem; magnaci akceptują dowolne wino.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład Filoksery',
            text: 'W roku 5 wychodzi Filoksera. Nikt nie produkuje. Gracze z zapasem wina robią Press Release i targi; gracz bez wina pomija Press Release.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: liczyć podwójny kafel jako dwa przy punktowaniu albo wpuszczać turystę do podwójnej piwnicy.',
          },
          {
            type: 'warning',
            text: 'Filoksera jest celowo brutalna — zostaw poza talią na pierwszą partię z modułem.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Trzy podwójne kafle (max 1/rodzaju); Filoksera = brak produkcji. P: Jak liczyć podwójny kafel? O: Jeden kafel, dwa miejsca. P: Czy turyści wchodzą do podwójnej piwnicy? O: Nie.',
          },
        ],
      },
    ],
  },
];
